// ============================================
// FILE: src/components/QuantityTextBox.jsx
// ============================================
//
// Typed quantity for apparel / gardening products, used in place of the
// 1 / 2 / 3 / 4 quick-pick pills (see utils/quantityHelpers).
//
// The field keeps its own text while the shopper types, so it can be cleared
// and retyped without snapping back to 1 mid-edit; every valid number is
// reported straight away, and an empty field is restored to 1 on blur.

import React, { useEffect, useState } from 'react';
import { QUANTITY_MIN, QUANTITY_MAX, parseQuantity } from '../utils/quantityHelpers';

const QuantityTextBox = ({
  value = QUANTITY_MIN,
  onChange,
  size = 'sm',          // 'sm' on product cards, 'md' in the product details modal
  label = 'Qty',
  className = '',
  style,
}) => {
  const [text, setText] = useState(String(value));

  // Follow outside changes (e.g. the modal resetting to 1 for a new product).
  useEffect(() => { setText(String(value)); }, [value]);

  const commit = (next) => {
    const qty = parseQuantity(next);
    if (qty !== null && onChange) onChange(qty);
    return qty;
  };

  const handleChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, String(QUANTITY_MAX).length);
    setText(digits);
    const qty = commit(digits);
    // Over the max: show the clamped value rather than the typed one.
    if (qty !== null && String(qty) !== digits) setText(String(qty));
  };

  const handleBlur = () => {
    const qty = parseQuantity(text) ?? QUANTITY_MIN;
    setText(String(qty));
    if (onChange) onChange(qty);
  };

  const sm = size === 'sm';

  return (
    <label
      className={`inline-flex items-center gap-1.5 ${className}`}
      style={style}
      onClick={(e) => e.stopPropagation()}
    >
      {label && (
        <span className={`${sm ? 'text-xs' : 'text-sm'} text-[#535766] font-medium`}>{label}</span>
      )}
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="off"
        aria-label="Quantity"
        value={text}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={(e) => e.target.select()}
        className={`text-center font-semibold text-[#282c3f] bg-white border border-[#eaeaec] rounded outline-none focus:border-[#ff3f6c] ${
          sm ? 'w-12 text-xs py-1' : 'w-20 text-base py-2'
        }`}
      />
    </label>
  );
};

export default QuantityTextBox;
